import { useCombobox } from 'downshift';
import type React from 'react';
import { useCallback, useId, useMemo, useState } from 'react';
import { matchesSearch } from '../../../../utils/searchText';
import { mergeRefs, SelectField, SelectFieldLabel, useFloatingSelectMenu } from '../../../select';
import { DeploymentMenu } from './DeploymentMenu';
import { Shell } from './DeploymentSelector.styles';
import { ChipTrigger, FieldTrigger, type TriggerWiring } from './DeploymentTriggers';
import { deploymentSelectorLabels } from './deploymentSelectorLabels';
import type { DeploymentItem, SelectorNoun, SelectorStatus } from './types';

export type { DeploymentItem } from './types';

const { ItemClick, InputKeyDownEnter } = useCombobox.stateChangeTypes;

const DEPLOYMENT_NOUN: SelectorNoun = { singular: 'deployment', plural: 'deployments' };

/** Symmetric gap (matches `spacing.r8`); Agent keeps shared hook defaults (24 / -8). */
const MAIN_AXIS_GAP_PX = 8;

export type DeploymentSelectorProps = {
  items: DeploymentItem[];
  /** The current selection. Single-select callers pass at most one item. */
  selectedItems: DeploymentItem[];
  onSelectionChange: (items: DeploymentItem[]) => void;
  /**
   * Hold several deployments at once: a row click toggles membership, the menu stays open
   * and the search term survives, so a run of matches can be picked without reopening.
   */
  multiple?: boolean;
  /**
   * Cap on how many may be held at once (multi mode only). Undefined means unlimited, so a
   * caller that does not set it is unaffected. At the cap unselected rows stop responding,
   * while selected ones stay live — otherwise the cap would be a dead end with no way back
   * under it.
   */
  maxSelection?: number;
  status: SelectorStatus;
  /** Open menu below trigger (welcome). Default: above (chat at bottom of viewport). */
  menuOpenBelow?: boolean;
  searchPlaceholder?: string;
  noun?: SelectorNoun;
  triggerIcon?: string;
  onMenuClose?: () => void;
  /**
   * When provided, filtering is the caller's job (the portal filters and ranks
   * server-side) and every keystroke is reported here. Omit to keep the local
   * substring filter.
   */
  onSearchChange?: (search: string) => void;
  /** Total matches across all pages; `items` may be one page of them. */
  totalCount?: number;
  /** A search is in flight (or pending debounce) — shows a spinner in the search field. */
  isSearching?: boolean;
  /** The caller forbids interaction — e.g. a dashboard cascade parent has no value yet. */
  disabled?: boolean;
  /**
   * Toolbar sizing: a fixed-width trigger whose label clamps instead of growing. In a row
   * that also holds a button group, a trigger sized by its own text reflows the row as the
   * selection changes — the placeholder is far wider than the chip that replaces it.
   */
  compact?: boolean;
  /**
   * Trigger skin: `chip` is the composer's pill, `field` the dashboard filter bar's legend
   * beside a bordered square box.
   */
  variant?: 'chip' | 'field';
  /** The legend beside the box in `field` mode, and the trigger's accessible name. */
  fieldLabel?: string;
};

export const DeploymentSelector: React.FC<DeploymentSelectorProps> = ({
  items,
  selectedItems,
  onSelectionChange,
  multiple = false,
  maxSelection,
  menuOpenBelow = false,
  searchPlaceholder,
  noun = DEPLOYMENT_NOUN,
  status,
  triggerIcon = 'Node-backend',
  onMenuClose,
  onSearchChange,
  totalCount,
  isSearching = false,
  disabled = false,
  compact = false,
  variant = 'chip',
  fieldLabel,
}) => {
  const [inputValue, setInputValue] = useState('');
  const fieldLabelId = useId();
  const selectedItem = selectedItems[0] ?? null;
  const hasSelection = selectedItems.length > 0;
  const isAtCap = multiple && typeof maxSelection === 'number' && selectedItems.length >= maxSelection;

  const labels = deploymentSelectorLabels({
    noun,
    selectedItems,
    itemCount: items.length,
    totalCount,
    status,
    isServerSearched: !!onSearchChange,
    multiple,
    maxSelection,
    searchPlaceholder,
  });

  const isSelected = useCallback(
    (item: DeploymentItem) => selectedItems.some((selected) => selected.id === item.id),
    [selectedItems],
  );

  /** Already-picked rows stay selectable at the cap so the selection can always be reduced. */
  const canToggle = useCallback((item: DeploymentItem) => !isAtCap || isSelected(item), [isAtCap, isSelected]);

  // An empty list means "no matches for this search" on the server-filtered path, not
  // "nothing to pick" — and the search box lives inside the menu, so disabling the trigger
  // would trap the user on a query they cannot clear.
  const isDisabled = disabled || status !== 'success' || (items.length === 0 && !onSearchChange);

  // With server-side search the caller already filtered and ranked; filtering again here
  // would drop rows the portal deliberately matched on account name.
  const filteredItems = useMemo(() => {
    if (onSearchChange) return items;
    if (!inputValue.trim()) return items;
    return items.filter((it) => matchesSearch(it.label, inputValue));
  }, [items, inputValue, onSearchChange]);

  /*
   * With a fleet in the thousands, `items` is one ranked page — which almost never contains
   * what the user already picked. Opening a multi-selection onto that page hides the very
   * rows they came back to check or remove, so the empty box means "show me my selection"
   * and typing means "search the fleet".
   */
  const isShowingSelection = multiple && !inputValue.trim() && selectedItems.length > 0;
  const rows = isShowingSelection ? selectedItems : filteredItems;

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, getInputProps, highlightedIndex, closeMenu } =
    useCombobox<DeploymentItem>({
      items: rows,
      itemToString: (item) => (item ? item.label : ''),
      selectedItem: multiple ? null : selectedItem,
      inputValue,
      onInputValueChange: ({ inputValue: next, type }) => {
        // A selection also pushes the chosen label into the input, because downshift
        // dispatches inputValue *after* selectedItem — which would overwrite the reset
        // in onSelectedItemChange and fire a search for the item the user just picked.
        if (type === ItemClick || type === InputKeyDownEnter) return;
        setInputValue(next ?? '');
        onSearchChange?.(next ?? '');
      },
      stateReducer: (state, { type, changes }) => {
        if (type === ItemClick || type === InputKeyDownEnter) {
          // Multi-select keeps the menu (and the highlight) where it is: the pick is one of
          // several, so closing would cost a reopen per deployment.
          return multiple
            ? { ...state, ...changes, isOpen: true, highlightedIndex: state.highlightedIndex }
            : { ...state, ...changes, isOpen: false };
        }
        return { ...state, ...changes };
      },
      onSelectedItemChange: ({ selectedItem: next }) => {
        if (!next) return;
        if (multiple) {
          // The cap is enforced here rather than by disabling the row, so a keyboard Enter
          // is refused on the same terms as a click; the dimming is only the affordance.
          if (!canToggle(next)) return;
          // Toggle. The search term is deliberately left alone so the remaining matches for
          // it stay on screen.
          onSelectionChange(
            isSelected(next) ? selectedItems.filter((item) => item.id !== next.id) : [...selectedItems, next],
          );
          return;
        }
        onSelectionChange([next]);
        setInputValue('');
        onSearchChange?.('');
        onMenuClose?.();
      },
    });

  const { refs: floatingRefs, floatingStyles } = useFloatingSelectMenu({
    isOpen,
    preferredBelow: menuOpenBelow,
    mainAxisAbovePx: MAIN_AXIS_GAP_PX,
    mainAxisBelowPx: MAIN_AXIS_GAP_PX,
  });

  const handleClear = useCallback(() => {
    onSelectionChange([]);
    closeMenu();
    onMenuClose?.();
  }, [onSelectionChange, closeMenu, onMenuClose]);

  const { ref: menuRef, style: menuStyle, ...menuProps } = getMenuProps({ 'aria-label': labels.placeholder });
  const { ref: toggleRef, ...toggleProps } = getToggleButtonProps(
    hasSelection && !disabled ? {} : { disabled: isDisabled },
  );

  // A selection keeps the trigger live mid-fetch: the menu is how you change or clear it.
  const wiring: TriggerWiring = {
    isOpen,
    isRefusing: !(hasSelection && !disabled) && isDisabled,
    refusalHint: labels.refusalHint,
    toggleProps,
    toggleRef,
    setReference: floatingRefs.setReference,
    selectionLabel: hasSelection ? labels.chip : null,
    onClear: handleClear,
  };

  const shell = (
    <Shell $compact={compact} $truncate={hasSelection}>
      {variant === 'field' ? (
        <FieldTrigger {...wiring} labelId={fieldLabelId} text={labels.field} />
      ) : (
        <ChipTrigger {...wiring} placeholder={labels.placeholder} compact={compact} icon={triggerIcon} />
      )}
      <DeploymentMenu
        isOpen={isOpen}
        rows={rows}
        itemCount={items.length}
        totalCount={totalCount}
        multiple={multiple}
        isShowingSelection={isShowingSelection}
        selectedCount={selectedItems.length}
        isSelected={isSelected}
        canToggle={canToggle}
        highlightedIndex={highlightedIndex}
        getItemProps={getItemProps}
        getInputProps={getInputProps}
        searchTerm={inputValue}
        isSearching={isSearching}
        noun={noun}
        labels={labels}
        panelRef={mergeRefs(floatingRefs.setFloating, menuRef)}
        panelProps={menuProps}
        panelStyle={{ ...menuStyle, ...floatingStyles }}
      />
    </Shell>
  );

  if (variant !== 'field') return shell;

  return (
    <SelectField>
      {fieldLabel && (
        <SelectFieldLabel id={fieldLabelId} variant="Basic" color="textSecondary">
          {fieldLabel}
        </SelectFieldLabel>
      )}
      {shell}
    </SelectField>
  );
};
